import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation13() {
  const { lesson, course, accent } = findLesson("piano-foundation", "13");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight><strong>Tonic (1-3-5)</strong> = C-E-G in C major. The "home" chord. Stable, settled.</Highlight>
        <Highlight><strong>Pre-Dominant / Sub-Dominant (1-4-6)</strong> = C-F-A in C major. The "moving" chord — leaves home but isn't far yet.</Highlight>
        <Highlight><strong>Dominant</strong> = either <strong>7-4-5</strong> (B-F-G) or <strong>7-2-5</strong> (B-D-G). The "tense" chord — magnetically pulls back to tonic.</Highlight>
        <Highlight>The three chords together cover <strong>every note in the scale</strong> — so any melody you play will fit one of them. That's why they work for almost any tune.</Highlight>
        <Highlight>You pick the chord by listening: when the melody hits a long note, that note needs a chord that <em>contains</em> it. Reverse-engineer from melody to harmony.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Why we need chords" title="The piano isn't a great melodic instrument on its own" accent={accent.hex}>
        <Pull>
          If I play the note E, whatever I do, I can't change it. It doesn't change, it
          doesn't bend pitch, I can't make it louder, it's just slowly dying a natural
          death.
        </Pull>

        <p>
          That's the practical reason this lesson exists. The melody alone is fragile on a
          piano — every note begins to die the moment it's struck. Stack a chord under it and
          the song fills out instantly. The piano stops trying to compete with a flute and
          starts being a one-person band.
        </p>
      </Insight>

      <Insight eyebrow="The three chords" title="Tonic · Pre-Dominant · Dominant" accent={accent.hex}>
        <Vocab>
          <Term name="Tonic" def="the 'home' chord. In C major, that's C-E-G. Built from scale degrees 1-3-5." />
          <Term name="Pre-Dominant (Sub-Dominant)" def="the 'leaving home' chord. C-F-A in C major. Scale degrees 1-4-6." />
          <Term name="Dominant" def="the 'pulled back home' chord. B-F-G in C major (7-4-5) — or B-D-G (7-2-5). Magnetically returns to the tonic." />
          <Term name="Musical gravity" def="term: any unstable thing eventually resolves. The Dominant has it; the Tonic is where gravity lands." />
        </Vocab>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "I · C" },
            { pitch: "E4", color: "#22c55e", label: "C" },
            { pitch: "G4", color: "#22c55e", label: "C" },
            { pitch: "F4", color: "#3b82f6", label: "IV · F" },
            { pitch: "A4", color: "#3b82f6", label: "F" },
            { pitch: "B4", color: "#f59e0b", label: "V · B" },
            { pitch: "D4", color: "#f59e0b", label: "G" },
          ]}
          height={200}
        />

        <p className="prose-nsm text-sm">
          <span className="text-emerald-300">Green</span> = Tonic (C-E-G).{" "}
          <span className="text-blue-300">Blue</span> = Pre-Dominant (notes shared with C and
          A). <span className="text-amber-300">Amber</span> = Dominant (B and D). The three
          chords between them touch every note of the scale.
        </p>
      </Insight>

      <Insight eyebrow="The emotional arc" title="Tonic feels stable · Dominant feels excited · Pre-Dominant sits between" accent={accent.hex}>
        <p>
          The three chords have very different emotional roles, and the framing of them is the easiest way to remember:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>
            <strong>Tonic</strong> — feels at home. Stable. Settled. The chord you can stay
            on forever and the song would still feel "fine".
          </li>
          <li>
            <strong>Pre-Dominant</strong> — has movement. Leaves the tonic, but not far.
            Often used as the chord that <em>walks toward</em> the dominant.
          </li>
          <li>
            <strong>Dominant</strong> — full of anticipation. Wants to go somewhere.{" "}
            <em>Magnetically</em> resolves to the tonic. That magnetism is musical gravity.
          </li>
        </ul>

        <KeyIdea>
          A standard ending in popular music: Sub-Dominant → Dominant → Tonic. Walk away from
          home, build tension, come back. That's the entire emotional grammar of three-chord
          songs.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="How to choose" title="Pick the chord that contains the long melody note" accent={accent.hex}>
        <p>
          A worked example — <strong>Twinkle Twinkle Little Star</strong> in
          C major. The melody alone won't tell you anything; the chord choice is dictated by
          where the melody <em>lands</em> on long notes.
        </p>

        <Steps title="The reverse-engineering process">
          <Step>
            <strong>Twinkle, twinkle</strong> — melody hits C and G. Both are in the Tonic
            (C-E-G). So Tonic.
          </Step>
          <Step>
            <strong>Little star</strong> — melody jumps to A. The Tonic doesn't have A. The
            Pre-Dominant (C-F-A) does. Switch to Pre-Dominant.
          </Step>
          <Step>
            <strong>Back down</strong> — melody comes through G. Tonic again (C-E-G has G).
          </Step>
          <Step>
            <strong>How I wonder what you are</strong> — melody hits F. Neither Tonic nor
            Pre-Dominant has F as a strong tone. Dominant (B-F-G) does. Switch.
          </Step>
          <Step>
            <strong>End on Tonic.</strong> Always.
          </Step>
        </Steps>

        <Pull>
          The chord choices are made primarily because of the melody. You can't change
          the melody, right? Otherwise it won't sound like Twinkle Twinkle in the first
          place.
        </Pull>
      </Insight>

      <Insight eyebrow="The practice loop" title="Take any tune and harmonise it" accent={accent.hex}>
        <Steps>
          <Step>
            <strong>Pick a melody you know.</strong> Twinkle Twinkle, Happy Birthday,
            Mary Had a Little Lamb — anything. Play it cleanly in the right hand, no chords
            yet.
          </Step>
          <Step>
            <strong>Find the long notes.</strong> The notes the melody sits on for more than
            a beat are where the chords need to land. The composer thought about chords while
            writing the melody — you're just reverse-engineering.
          </Step>
          <Step>
            <strong>Pick from the three.</strong> Tonic, Pre-Dominant or Dominant — whichever
            chord <em>contains</em> the long melody note.
          </Step>
          <Step>
            <strong>Play melody + chord together.</strong> Right hand keeps the melody; left
            hand holds the matching chord. If the chord clashes, you picked the wrong one —
            try a different one of the three.
          </Step>
          <Step>
            <strong>Move keys.</strong> Once the song works in C major, transpose to G or F
            or D. The same three chords (I, IV, V) live in every scale.
          </Step>
        </Steps>

        <Tip>
          For longer melody notes — like the held "you" in "Happy Birthday to <em>you</em>"
          — the chord change almost certainly belongs there. Composers write long notes as
          chord landing points more often than not.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-14"
        label="Lesson 14 · Left Hand Patterns (Chords) - PART 2"
        why="Right now the LH is just holding each chord. The next lesson teaches it the same four rhythm patterns from Lesson 12 — but with full chords instead of just root + octave."
      />
    </LessonShell>
  );
}
